import Immutable from 'immutable';
import { NODE_TYPE, STATUS } from './constants';
import { buildAnonymizer, getIsAnonymized } from 'edrolo/domains/anonymizer';
import * as assemblageUtils from './assemblageUtils';
//
// assemblages
//

export const getCourseAssemblageStatus = (state, classId) =>
  state.bySchoolClass.getIn(
    ['assemblages', `${classId}`, 'status'],
    STATUS.UNINITIALIZED
  );

export const getCourseAssemblageFailureReason = (state, classId) =>
  state.bySchoolClass.getIn(['assemblages', `${classId}`, 'failureReason'], '');

export const getCourseAssemblage = (state, classId) =>
  state.bySchoolClass.getIn(
    ['assemblages', `${classId}`, 'data'],
    Immutable.Map()
  );

export const isCourseFeatureEnabledInFeatureSet = (
  state,
  classId,
  featureSet,
  feature
) =>
  state.bySchoolClass
    .getIn(
      [
        'assemblages',
        `${classId}`,
        'data',
        'course',
        'feature_sets',
        featureSet,
      ],
      Immutable.List()
    )
    .includes(feature);

export const isStudentNotesEnabled = (state, classId) =>
  state.bySchoolClass.getIn([
    'assemblages',
    `${classId}`,
    'data',
    'course',
    'show_student_notes',
  ]);

/*
 * Get the first lesson in a course by recursively descending into the
 * course tree.
 *
 * TODO remove this and use getFlatLessons(state, classId)[0] instead.
 */
export const getFirstLessonInCourse = (state, classId) => {
  const getFirstLessonFromTree = (node, content) => {
    if (!node || !content) {
      return null;
    }

    if (node.get('node_type') === 'LESSON') {
      const lesson = content.getIn(['lessons', `${node.get('value_id')}`]);
      return lesson.get('is_published') ? lesson : null;
    }

    const children = node.get('children');
    if (!children || children.size === 0) {
      return null;
    }

    const lesson = null;
    for (const node of children) {
      const lesson = getFirstLessonFromTree(node, content);
      if (lesson) {
        return lesson;
      }
    }
    return lesson;
  };

  const courseRootNode = state.bySchoolClass.getIn([
    'assemblages',
    `${classId}`,
    'data',
    'node',
  ]);

  const content = state.bySchoolClass.getIn([
    'assemblages',
    `${classId}`,
    'data',
    'content',
  ]);

  return getFirstLessonFromTree(courseRootNode, content);
};

//
// classDetail
//

export const getClassDetailStatus = (state, classId) =>
  state.bySchoolClass.getIn(
    ['classDetail', `${classId}`, 'status'],
    STATUS.UNINITIALIZED
  );

export const getClassDetail = (state, classId) =>
  state.bySchoolClass.getIn(
    ['classDetail', `${classId}`, 'data'],
    Immutable.Map()
  );

export const isClassFeatureEnabled = (state, classId, feature) =>
  state.bySchoolClass
    .getIn(['classDetail', `${classId}`, 'data', 'features'], Immutable.List())
    .includes(feature);

const studentAnonymizer = buildAnonymizer({
  seedResolver: 'user_id',
  schemaFields: [
    { key: 'last_name', type: 'last_name' },
    { key: 'display_name', type: 'full_name' },
    { key: 'first_name', type: 'first_name' },
  ],
});

export const getClassDetailStudents = (state, classId) => {
  const students = state.bySchoolClass.getIn(
    ['classDetail', `${classId}`, 'data', 'students'],
    Immutable.Map()
  );
  return getIsAnonymized(state) ? students.map(studentAnonymizer) : students;
};

export const getClassDetailActivationLink = (state, classId) => {
  const activationLink = state.bySchoolClass.getIn(
    ['classDetail', `${classId}`, 'data', 'activation_link'],
    null
  );
  return activationLink;
};

//
// classProgress
//

export const getClassProgressStatus = (state, classId) =>
  state.bySchoolClass.getIn(
    ['classProgress', `${classId}`, 'status'],
    STATUS.UNINITIALIZED
  );

export const getClassProgress = (state, classId) =>
  state.bySchoolClass.getIn(
    ['classProgress', `${classId}`, 'data'],
    Immutable.Map()
  );

export const getClassProgressLesson = (state, classId, lessonId) =>
  getClassProgress(state, classId).getIn(
    ['lessons', `${lessonId}`],
    Immutable.Map()
  );

export const getClassProgressExam = (state, classId, examId) =>
  getClassProgress(state, classId).getIn(
    ['exams', `${examId}`],
    Immutable.Map()
  );

//
// courseProgress
//
export const getCourseProgressStatus = (state, classId) =>
  state.bySchoolClass.getIn(
    ['courseProgress', `${classId}`, 'status'],
    STATUS.UNINITIALIZED
  );

export const getCourseProgress = (state, classId) =>
  state.bySchoolClass.getIn(
    ['courseProgress', `${classId}`, 'data'],
    Immutable.Map()
  );

export const getCourseProgressLesson = (state, classId, lessonId) =>
  getCourseProgress(state, classId).getIn(
    ['lessons', `${lessonId}`],
    Immutable.Map()
  );

export const getCourseProgressExam = (state, classId, examId) =>
  getCourseProgress(state, classId).getIn(
    ['exams', `${examId}`],
    Immutable.Map()
  );

// determines whether an exam is locked.
// note: This is stored in a different place in the redux state for teachers and students
//       because the information is supplied from different api endpoints.
export const isExamLocked = (state, classId, examId, isTeacherOfClass) => {
  if (isTeacherOfClass) {
    return state.bySchoolClass.getIn(
      [
        'classProgress',
        `${classId}`,
        'data',
        'exams',
        `${examId}`,
        'is_locked',
      ],
      false
    );
  }

  return state.bySchoolClass
    .getIn(
      ['courseProgress', `${classId}`, 'data', 'locked_exam_ids'],
      Immutable.Set()
    )
    .has(`${examId}`);
};

export const getFlatLessons = (state, classId) => {
  const assemblage = getCourseAssemblage(state, classId);
  const lessonNodes = assemblageUtils.findChildNodesByType(
    assemblage.get('node'),
    NODE_TYPE.LESSON
  );
  return lessonNodes.map((lessonNode) =>
    assemblage.getIn(['content', 'lessons', `${lessonNode.get('value_id')}`])
  );
};

export const getPrevAndNextLessonIds = (state, classId, lessonId) => {
  const flatLessons = getPublishedFlatLessonsWithChunks(state, classId);
  if (flatLessons.size === 0) {
    return Immutable.Map();
  }

  const currentLesson = flatLessons.find(
    (l) => l.get('id') === Number(lessonId)
  );
  if (!currentLesson) {
    return Immutable.Map();
  }

  const currentLessonIndex = flatLessons.indexOf(currentLesson);
  const prevLessonIndex =
    currentLessonIndex === 0 ? null : currentLessonIndex - 1;
  const nextLessonIndex =
    currentLessonIndex === flatLessons.size - 1 ? null : currentLessonIndex + 1;

  const prevLessonId =
    prevLessonIndex != null && flatLessons.get(prevLessonIndex).get('id');
  const nextLessonId =
    nextLessonIndex != null && flatLessons.get(nextLessonIndex).get('id');

  return Immutable.Map({
    prevLessonId,
    nextLessonId,
  });
};

export const getPrevAndNextQuestionSetIds = (state, classId, examId) => {
  let prevQuestionSetId = null;
  let found = false;
  let nextQuestionSetId = null;

  getFlatLessons(state, classId).forEach((lesson) => {
    const currentQuestionSetId = lesson.getIn(['question_set', 'id'], null);
    if (found) {
      if (currentQuestionSetId) {
        nextQuestionSetId = currentQuestionSetId;
        return false; // In immutable, this stops the forEach iteration.
      }
    } else if (currentQuestionSetId === Number(examId)) {
      found = true;
    } else if (currentQuestionSetId) {
      prevQuestionSetId = currentQuestionSetId;
    } else {
      // no question set in this lesson
    }
  });

  return Immutable.Map({
    prevQuestionSetId,
    nextQuestionSetId,
  });
};

export const getStudentName = (state, classId, studentProfileId) => {
  const student = state.bySchoolClass.getIn(
    ['classDetail', `${classId}`, 'data', 'students', `${studentProfileId}`],
    Immutable.Map()
  );
  return getIsAnonymized(state)
    ? studentAnonymizer(student).get('display_name')
    : student.get('display_name');
};

export const getStudentFirstName = (state, classId, studentProfileId) => {
  const student = state.bySchoolClass.getIn(
    ['classDetail', `${classId}`, 'data', 'students', `${studentProfileId}`],
    Immutable.Map()
  );
  return getIsAnonymized(state)
    ? studentAnonymizer(student).get('first_name')
    : student.get('first_name');
};

export const getPublishedFlatLessonsWithChunks = (state, classId) => {
  const flatLessons = getFlatLessons(state, classId);
  return flatLessons.filter(
    (lesson) =>
      lesson.get('is_published') &&
      lesson.get('chunk_count') &&
      !lesson.get('is_coming_soon')
  );
};

export const getCourse = (state, classId) => {
  const assemblage = getCourseAssemblage(state, classId);
  return assemblage.get('course');
};

export const getTopicLessons = (
  state,
  classId,
  topicId,
  publishedOnly = true
) => {
  const assemblage = getCourseAssemblage(state, classId);
  const topicNode = assemblageUtils.findSectionNode(assemblage, topicId, true);
  const lessonNodes = assemblageUtils.findChildNodesByType(
    topicNode,
    NODE_TYPE.LESSON
  );
  let lessons = lessonNodes.map((lessonNode) =>
    assemblage.getIn(['content', 'lessons', `${lessonNode.get('value_id')}`])
  );

  if (publishedOnly) {
    lessons = lessons.filter((lesson) => lesson.get('is_published'));
  }

  return lessons;
};

// used by the Lesson component in studentCourseProgress to ensure that
// progress pages for courses with question sets are styled consistently
export const getCourseHasQuestionSets = (state, classId) => {
  const flatLessons = getFlatLessons(state, classId);
  return flatLessons.some((lesson) => lesson.get('question_set'));
};

export const getTopics = (state, classId, publishedOnly = true) => {
  const assemblage = getCourseAssemblage(state, classId);
  const topicNodes = assemblageUtils.findTopicNodes(assemblage);
  const topics = topicNodes.map((topicNode) =>
    assemblage.getIn(['content', 'sections', `${topicNode.get('value_id')}`])
  );

  if (publishedOnly) {
    const publishedTopics = Immutable.Map(
      topics
        .filter((topic) => topic.get('is_published'))
        .map((publishedTopic) => [
          `${publishedTopic.get('id')}`,
          publishedTopic,
        ])
    );

    return publishedTopics;
  }

  return Immutable.Map(topics.map((topic) => [`${topic.get('id')}`, topic]));
};

export const getTopicExamsForTopic = (
  state,
  classId,
  topicId,
  publishedOnly = true
) => {
  const assemblage = getCourseAssemblage(state, classId);
  const topicNode = assemblage.getIn(['node', 'children'])?.find((node) => {
    return `${node.get('value_id')}` === topicId;
  });

  const examNodes = assemblageUtils.findChildNodesByType(
    topicNode,
    NODE_TYPE.EXAM
  );
  const topicExams = examNodes.map((examNode) =>
    assemblage.getIn(['content', 'exams', `${examNode.get('value_id')}`])
  );

  if (publishedOnly) {
    return topicExams.filter((topicExam) => topicExam.get('is_published'));
  }

  return topicExams;
};

export const getAssemblageExam = (state, classId, examId) => {
  // Returns exam content object from the assemblage - shape of examPropTypes
  const assemblage = getCourseAssemblage(state, classId);
  return assemblage.getIn(['content', 'exams', `${examId}`]);
};

export const getHasCourseSuccessMeasure = (state, classId) => {
  const flatLessons = getFlatLessons(state, classId);
  return flatLessons.some((lesson) => lesson.get('has_success_measure'));
};

export const getFlatStudents = (state, classId) => {
  const students = getClassDetailStudents(state, classId);
  return students.valueSeq();
};

export const getPrevAndNextStudentProfileIds = (state, classId, profileId) => {
  const flatStudents = getFlatStudents(state, classId);

  if (flatStudents.size === 0) {
    return Immutable.Map();
  }

  const currentStudent = flatStudents.find(
    (s) => s.get('profile_id') === Number(profileId)
  );

  if (!currentStudent) {
    return Immutable.Map();
  }

  const currentStudentIndex = flatStudents.indexOf(currentStudent);
  const prevStudentIndex =
    currentStudentIndex === 0 ? null : currentStudentIndex - 1;
  const nextStudentIndex =
    currentStudentIndex === flatStudents.size - 1
      ? null
      : currentStudentIndex + 1;

  const prevStudentProfileId =
    prevStudentIndex != null &&
    flatStudents.get(prevStudentIndex).get('profile_id');
  const nextStudentProfileId =
    nextStudentIndex != null &&
    flatStudents.get(nextStudentIndex).get('profile_id');

  return Immutable.Map({
    prevStudentProfileId,
    nextStudentProfileId,
  });
};
